import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link2Off } from 'lucide-react';
import { TEST_LINK_PARAM, redeemTestLink } from '@/api/directSale';
import { EmptyState, LoadingState } from '@/components/ui';
import { PREVIEW_FRAME_PARAM } from '@/lib/preview-frame';

/**
 * Redemptions per token, shared by every mount in one page load: StrictMode
 * mounts twice, and one fresh entry link per visit is enough.
 */
const redemptions = new Map<string, Promise<string | null>>();

function entryUrlFor(token: string, language: string): Promise<string | null> {
  const running = redemptions.get(token);
  if (running) return running;

  const redemption = redeemTestLink(token)
    .then(
      ({ shop, ts, sig }) =>
        `/direct-sale?${new URLSearchParams({
          shop,
          ts: String(ts),
          sig,
          lang: language,
          // Its own storage and no seller tokens (lib/preview-frame.ts): the
          // seller often opens the link in the browser they work in.
          [PREVIEW_FRAME_PARAM]: '1',
          // Lets the configurator renew its entry on a later reload
          // (api/directSale.ts, renewTestEntry).
          [TEST_LINK_PARAM]: token,
        }).toString()}`
    )
    // Expired, forged, revoked or unreachable end alike: only a new link helps.
    .catch(() => null);
  redemptions.set(token, redemption);
  return redemption;
}

/**
 * `/try/<token>`: the seller's shareable test link. Trades the 30-day token
 * for a fresh five-minute entry link and replaces itself with the bare
 * configurator — full window, no seller area around it — so the link can be
 * opened on a desktop or an iPhone alike. No guard: whoever holds the link
 * may try the configurator, as a shop visitor may.
 */
export function TryDirectSalePage() {
  const { t, i18n } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (!token) {
      setInvalid(true);
      return;
    }
    void entryUrlFor(token, i18n.language).then((url) => {
      // replace(): "back" from the configurator leaves the link, rather than
      // landing here and redeeming it once more.
      if (url) window.location.replace(url);
      else setInvalid(true);
    });
  }, [token, i18n.language]);

  if (invalid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-4">
        <EmptyState
          icon={<Link2Off className="h-6 w-6" aria-hidden="true" />}
          title={t('directSale.testLink.invalidTitle')}
          description={t('directSale.testLink.invalidDescription')}
        />
      </div>
    );
  }

  return <LoadingState fullscreen label={t('common.loading')} />;
}
